import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Send, MessageSquare, ChevronDown, TrendingUp,
  TrendingDown, Minus, AlertTriangle, Play, FlaskConical, Code2, Activity, Zap, Undo2,
  History, RotateCcw, Check,
} from 'lucide-react';
import { useChatWithStrategy, useStatelessBacktest, useChatSession, type ChatResponse, type ChatSessionMessage } from '@/hooks/use-strategies';
import { useJob } from '@/hooks/use-job';
import { EquityCurve } from '@/components/equity-curve';
import { Modal } from '@/components/ui/modal';
import { TradeHistoryModal } from '@/components/ui/trade-history-modal';
import { condToSentence, fmtLot } from '@/lib/condition-text';
import type { BacktestReport } from '@/types/api';
import type { ConditionEngineRules } from './editor-types';

// ─── Types ───────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  rulesApplied?: boolean;
  rulesSnapshot?: ConditionEngineRules;
  previousRules?: ConditionEngineRules; // rules before this AI change, for undo
  options?: string[];
  keyPoints?: Array<{ label: string; value: string; sentiment: 'positive' | 'negative' | 'neutral' }>;
  backtestJobId?: string;
  backtestDays?: number;
  backtestAction?: boolean;
  backtestSummary?: string; // stored backtest summary for conversation history
}

// ─── Simple Markdown Renderer ────────────────────────

function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">
          {listItems.map((li, i) => <li key={i}>{inlineFormat(li)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  const inlineFormat = (s: string): React.ReactNode => {
    // Bold **text**, italic *text*, code `text`, brand ForgeAlpha
    const parts: React.ReactNode[] = [];
    let remaining = s;
    let key = 0;
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|ForgeAlpha)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        parts.push(remaining.slice(lastIndex, match.index));
      }
      if (match[2]) parts.push(<strong key={key++} className="font-bold">{match[2]}</strong>);
      else if (match[3]) parts.push(<em key={key++} className="italic">{match[3]}</em>);
      else if (match[4]) parts.push(<code key={key++} className="bg-surface px-1 rounded text-xs font-mono">{match[4]}</code>);
      else if (match[0] === 'ForgeAlpha') parts.push(<span key={key++}><span className="text-orange font-bold">Forge</span>Alpha</span>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) parts.push(remaining.slice(lastIndex));
    return parts.length > 0 ? parts : s;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^[-*•]\s/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-*•]\s+/, ''));
    } else {
      flushList();
      if (trimmed === '') {
        elements.push(<div key={`sp-${elements.length}`} className="h-1.5" />);
      } else if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
        // Section header: a line that is entirely bold, e.g. **Risk Assessment**
        const title = trimmed.replace(/^\*\*|\*\*$/g, '');
        elements.push(
          <p key={`h-${elements.length}`} className="font-bold text-ink mt-1.5 first:mt-0">
            {title}
          </p>
        );
      } else {
        elements.push(<p key={`p-${elements.length}`}>{inlineFormat(trimmed)}</p>);
      }
    }
  }
  flushList();
  return <>{elements}</>;
}

// ─── Template-aware presets ──────────────────────────

const TEMPLATE_PRESETS: Record<string, string[]> = {
  'martingale-grid': [
    'Optimize the grid step size for XAUUSD',
    'Reduce risk by lowering the martingale multiplier',
    'Add a trailing stop to protect profits',
    'Adjust the take profit target for higher returns',
    'Add a time-based exit for weekends',
  ],
  'reverse-martingale-grid': [
    'Increase the multiplier for faster lot reduction',
    'Add RSI filter to avoid entering in strong trends',
    'Adjust the grid step for volatile markets',
    'Add a maximum drawdown exit rule',
    'Switch to ATR-based grid spacing',
  ],
  'conservative-grid': [
    'Add more grid levels for wider coverage',
    'Increase lot size slightly for better returns',
    'Add a trend filter to avoid ranging markets',
    'Set a daily profit target exit',
    'Add trailing stop to lock in gains',
  ],
  'macd-crossover': [
    'Tighten the trailing stop for less drawdown',
    'Add RSI confirmation to filter false signals',
    'Change to a faster timeframe for more trades',
    'Add a partial close at first target',
    'Increase ADX threshold to filter weak trends',
  ],
  'london-session-scalper': [
    'Extend to include New York overlap hours',
    'Add Bollinger Bands for better entries',
    'Tighten the take profit for faster exits',
    'Add a maximum daily loss limit',
    'Switch to RSI instead of Stochastic',
  ],
  'cci-mean-reversion': [
    'Adjust CCI thresholds for more signals',
    'Add a grid to average into positions',
    'Increase the partial close percentage',
    'Add a time-based exit after 24 candles',
    'Switch to Bollinger Bands for mean reversion',
  ],
  'scaled-exit-trend': [
    'Add a second scale-out at 2% profit',
    'Switch to SMA crossover instead of EMA',
    'Lower ADX threshold for earlier entries',
    'Add RSI overbought/oversold exit',
    'Use percent-based trailing stop instead of ATR',
  ],
};

const DEFAULT_PRESETS = [
  'Martingale grid that buys dips',
  'RSI mean reversion strategy',
  'Trend following with MA crossover',
  'Low-risk passive income strategy',
  'Scalping with tight stops',
  'Grid strategy for gold (XAUUSD)',
];

function getPresetsForTemplate(templateId?: string): string[] {
  if (!templateId) return DEFAULT_PRESETS;
  return TEMPLATE_PRESETS[templateId] ?? DEFAULT_PRESETS;
}

// ─── Rules Preview Card ─────────────────────────────

export function RulesPreviewCard({ rules }: { rules: ConditionEngineRules }) {
  const [expanded, setExpanded] = useState(false);

  const entryRules = rules.entry?.rules ?? [];
  const exitRules = rules.exit?.rules ?? [];
  const grid = rules.entry?.grid;
  const trailing = rules.exit?.trailingStop;
  const eqSL = rules.exit?.equityStopLossPct;

  return (
    <div className="bg-surface-2/80 rounded-md mt-2 overflow-hidden border border-glass-border/50">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 hover:bg-white/[0.02] transition-colors cursor-pointer"
      >
        <span className="flex items-center gap-1.5 text-[9px] font-bold text-ink-3 uppercase tracking-wider">
          <Code2 size={10} className="text-cyan" />
          Strategy Rules
        </span>
        <ChevronDown size={12} className={`text-ink-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-2 text-[10px]">
          {/* Timeframe */}
          <div className="text-ink-4">
            Timeframe: <span className="font-mono font-bold text-ink">{rules.market_data?.timeframe ?? '—'}</span>
          </div>

          {/* Entry Rules */}
          {entryRules.length > 0 && (
            <div>
              <div className="text-[9px] font-bold text-green uppercase tracking-wider mb-1">Entry Rules</div>
              {entryRules.map((r: any, i: number) => (
                <div key={r.id ?? i} className="pl-2 border-l-2 border-green/30 mb-1.5 space-y-0.5">
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-ink">{r.direction}</span>
                    <span className="text-ink-4">·</span>
                    <span className="text-ink-3">{fmtLot(r.lotSizing)}</span>
                  </div>
                  {r.conditions && (
                    <div className="leading-relaxed">{condToSentence(r.conditions)}</div>
                  )}
                  {r.buyCondition && (
                    <div className="leading-relaxed">
                      <span className="text-green font-bold text-[9px]">BUY:</span>{' '}
                      {condToSentence(r.buyCondition)}
                    </div>
                  )}
                  {r.sellCondition && (
                    <div className="leading-relaxed">
                      <span className="text-red font-bold text-[9px]">SELL:</span>{' '}
                      {condToSentence(r.sellCondition)}
                    </div>
                  )}
                  {(r.maxPositions || r.cooldownCandles) && (
                    <div className="text-ink-4">
                      {r.maxPositions && <span>Max: {r.maxPositions}</span>}
                      {r.maxPositions && r.cooldownCandles && <span> · </span>}
                      {r.cooldownCandles && <span>Cooldown: {r.cooldownCandles} candles</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Grid */}
          {grid?.enabled && (
            <div>
              <div className="text-[9px] font-bold text-orange uppercase tracking-wider mb-1">Grid</div>
              <div className="pl-2 border-l-2 border-orange/30 text-ink-3 space-y-0.5">
                <div>
                  Step: <span className="font-mono font-bold text-ink">{grid.stepValue}</span>{' '}
                  <span className="text-ink-4">({grid.stepType})</span>
                  {' · '}Levels: <span className="font-mono font-bold text-ink">{grid.maxGridLevels}</span>
                </div>
                <div>Sizing: {fmtLot(grid.lotSizing)}</div>
              </div>
            </div>
          )}

          {/* Exit Rules */}
          {exitRules.length > 0 && (
            <div>
              <div className="text-[9px] font-bold text-red uppercase tracking-wider mb-1">Exit Rules</div>
              {exitRules.map((r: any, i: number) => (
                <div key={r.id ?? i} className="pl-2 border-l-2 border-red/30 mb-1.5 space-y-0.5">
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-ink">{r.action}</span>
                    {r.closePct != null && (
                      <span className="text-ink-3">({r.closePct}%)</span>
                    )}
                    {r.name && <span className="text-ink-4">· {r.name}</span>}
                  </div>
                  {r.conditions && (
                    <div className="leading-relaxed">{condToSentence(r.conditions)}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Trailing Stop */}
          {trailing?.enabled && (
            <div className="text-ink-3">
              Trailing: <span className="font-bold text-ink">{trailing.type}</span>{' '}
              <span className="font-mono">{trailing.value}</span>
              {trailing.atrPeriod && <span className="text-ink-4"> (ATR {trailing.atrPeriod})</span>}
            </div>
          )}

          {/* Equity SL */}
          {eqSL != null && eqSL < 100 && (
            <div className="text-ink-3">
              Equity SL: <span className="font-mono font-bold text-red">{eqSL}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Slim backtest report for API transfer ───────────
// Keeps trades + downsampled equity curve (~100 points) to stay under body limit

function slimReportForChat(report: BacktestReport): Record<string, unknown> {
  const { equityCurve, ...rest } = report;
  // Downsample equity curve to ~100 points
  let slimCurve = equityCurve;
  if (equityCurve?.length > 100) {
    const step = Math.floor(equityCurve.length / 100);
    slimCurve = equityCurve.filter((_, i) => i % step === 0 || i === equityCurve.length - 1);
  }
  return { ...rest, equityCurve: slimCurve };
}

// ─── Backtest Result Card (chat inline) ──────────────

function BacktestResultCard({ jobId, onComplete, onAnalyze }: { jobId: string; onComplete?: (report: BacktestReport) => void; onAnalyze?: (report: BacktestReport) => void }) {
  const job = useJob<BacktestReport>('backtest', jobId);
  const calledRef = useRef(false);
  const [showTrades, setShowTrades] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);

  useEffect(() => {
    if (job.isComplete && job.result && !calledRef.current) {
      calledRef.current = true;
      onComplete?.(job.result);
    }
  }, [job.isComplete, job.result, onComplete]);

  if (job.isLoading) {
    return (
      <div className="bg-surface-2 rounded-md p-3 mt-2">
        <div className="flex items-center gap-2 text-[10px] text-ink-3">
          <div className="flex gap-1">
            <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:0ms]" />
            <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:300ms]" />
          </div>
          <span>{job.progress?.stage === 'fetching_data' ? 'Fetching market data...' : job.progress?.stage === 'simulating' ? 'Running simulation...' : 'Preparing backtest...'}</span>
        </div>
      </div>
    );
  }

  if (job.isFailed) {
    return (
      <div className="bg-red/10 border border-red/20 rounded-md p-3 mt-2">
        <p className="text-[10px] text-red font-semibold flex items-center gap-1">
          <AlertTriangle size={10} />
          Backtest failed: {job.error || 'Unknown error'}
        </p>
      </div>
    );
  }

  if (!job.isComplete || !job.result) return null;

  const r = job.result;
  const netProfit = r.netProfit ?? 0;
  const returnPct = r.returnPct ?? 0;
  const winRate = r.winRate ?? 0;
  const maxDrawdown = r.maxDrawdown ?? 0;
  const profitFactor = r.profitFactor ?? 0;
  const sharpeRatio = r.sharpeRatio ?? 0;
  const avgTradePnl = r.avgTradePnl ?? 0;
  const isProfit = netProfit >= 0;

  return (
    <div className="bg-surface-2 rounded-md overflow-hidden mt-2">
      {r.equityCurve?.length > 2 && (
        <EquityCurve data={r.equityCurve} initialCapital={r.initialCapital} height={100} />
      )}
      {/* Config metadata */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 pt-2.5 pb-1">
        {r.symbol && <span className="text-[10px] font-bold text-ink-2">{r.symbol}</span>}
        {r.dataTimeframe && <span className="text-[10px] font-semibold text-ink-3">{r.dataTimeframe.toUpperCase()}</span>}
        <span className="text-[10px] font-semibold text-ink-3">${r.initialCapital.toLocaleString()}</span>
        {r.leverage != null && r.leverage > 1 && <span className="text-[10px] font-semibold text-ink-3">{r.leverage}:1</span>}
        {r.dataStartDate && r.dataEndDate && (
          <span className="text-[9px] text-ink-4">
            {new Date(r.dataStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(r.dataEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 p-3">
        <MiniStat label="Net P&L" value={`${isProfit ? '+' : ''}$${netProfit.toFixed(2)}`} sentiment={isProfit ? 'positive' : 'negative'} />
        <MiniStat label="Return" value={`${isProfit ? '+' : ''}${returnPct.toFixed(1)}%`} sentiment={isProfit ? 'positive' : 'negative'} />
        <MiniStat label="Win Rate" value={`${winRate.toFixed(1)}%`} sentiment={winRate >= 50 ? 'positive' : 'negative'} />
        <MiniStat label="Trades" value={String(r.totalTrades ?? 0)} sentiment="neutral" />
        <MiniStat label="Max DD" value={`${maxDrawdown.toFixed(1)}%`} sentiment={maxDrawdown < 20 ? 'positive' : maxDrawdown < 50 ? 'neutral' : 'negative'} />
        <MiniStat label="Profit Factor" value={profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)} sentiment={profitFactor >= 1.5 ? 'positive' : profitFactor >= 1 ? 'neutral' : 'negative'} />
        <MiniStat label="Sharpe" value={sharpeRatio.toFixed(2)} sentiment={sharpeRatio >= 1 ? 'positive' : sharpeRatio >= 0 ? 'neutral' : 'negative'} />
        <MiniStat label="Avg Trade" value={`$${avgTradePnl.toFixed(2)}`} sentiment={avgTradePnl >= 0 ? 'positive' : 'negative'} />
      </div>
      {r.warnings && r.warnings.length > 0 && (
        <div className="px-3 pb-2">
          {r.warnings.slice(0, 2).map((w, i) => (
            <p key={i} className="text-[9px] text-amber flex items-start gap-1 mt-1">
              <AlertTriangle size={9} className="mt-0.5 flex-shrink-0" />
              {w.message}
            </p>
          ))}
        </div>
      )}
      <div className="flex border-t border-glass-border/50">
        {r.trades && r.trades.length > 0 && (
          <button
            type="button"
            onClick={() => setShowTrades(true)}
            className="flex-1 text-[10px] text-orange font-semibold cursor-pointer flex items-center justify-center gap-1 py-2 hover:bg-white/[0.02] transition-colors outline-none focus:outline-none"
          >
            <Activity size={10} />
            View {r.totalTrades} trades
          </button>
        )}
        {onAnalyze && !analyzed && (
          <button
            type="button"
            onClick={() => { setAnalyzed(true); onAnalyze(r); }}
            className={`flex-1 text-[10px] text-orange font-semibold cursor-pointer flex items-center justify-center gap-1 py-2 hover:bg-white/[0.02] transition-colors outline-none focus:outline-none ${r.trades?.length ? 'border-l border-glass-border/50' : ''}`}
          >
            <FlaskConical size={10} />
            Analyze with AI
          </button>
        )}
      </div>

      <TradeHistoryModal
        open={showTrades}
        onClose={() => setShowTrades(false)}
        trades={r.trades ?? []}
        totalTrades={r.totalTrades ?? 0}
        netProfit={r.netProfit ?? 0}
      />
    </div>
  );
}

function MiniStat({ label, value, sentiment }: { label: string; value: string; sentiment: 'positive' | 'negative' | 'neutral' }) {
  const color = sentiment === 'positive' ? 'text-green' : sentiment === 'negative' ? 'text-red' : 'text-ink';
  return (
    <div>
      <p className="text-[9px] text-ink-3 uppercase tracking-wider">{label}</p>
      <p className={`text-[12px] font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ─── Analysis Card ───────────────────────────────────

function AnalysisCard({ keyPoints }: { keyPoints: Array<{ label: string; value: string; sentiment: 'positive' | 'negative' | 'neutral' }> }) {
  const sentimentIcon = (s: string) => {
    if (s === 'positive') return <TrendingUp size={10} className="text-green" />;
    if (s === 'negative') return <TrendingDown size={10} className="text-red" />;
    return <Minus size={10} className="text-ink-3" />;
  };

  return (
    <div className="bg-surface-2 rounded-md p-3 mt-2 space-y-1.5">
      {keyPoints.map((kp, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {sentimentIcon(kp.sentiment)}
            <span className="text-[10px] text-ink-3">{kp.label}</span>
          </div>
          <span className={`text-[10px] font-bold ${
            kp.sentiment === 'positive' ? 'text-green' : kp.sentiment === 'negative' ? 'text-red' : 'text-ink'
          }`}>{kp.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Backtest Modal ─────────────────────────────────

interface BacktestInitialParams {
  days?: number;
  capital?: number;
  leverage?: number;
}

function BacktestModal({
  open,
  onClose,
  symbol,
  currentRules,
  onJobStarted,
  initialParams,
}: {
  open: boolean;
  onClose: () => void;
  symbol: string;
  currentRules: ConditionEngineRules;
  onJobStarted: (jobId: string, days: number) => void;
  initialParams?: BacktestInitialParams;
}) {
  const [days, setDays] = useState(initialParams?.days ?? 30);
  const [capital, setCapital] = useState(String(initialParams?.capital ?? 10000));
  const [leverage, setLeverage] = useState(String(initialParams?.leverage ?? 100));

  // Update when initialParams change (e.g., AI suggests params)
  useEffect(() => {
    if (initialParams) {
      if (initialParams.days) setDays(initialParams.days);
      if (initialParams.capital) setCapital(String(initialParams.capital));
      if (initialParams.leverage) setLeverage(String(initialParams.leverage));
    }
  }, [initialParams]);
  const statelessBacktest = useStatelessBacktest();

  const handleRun = async () => {
    if (!symbol) return;
    try {
      const result = await statelessBacktest.mutateAsync({
        rules: currentRules,
        symbol,
        days,
        capital: parseFloat(capital) || 10000,
        leverage: parseInt(leverage) || 100,
      });
      onJobStarted(result.jobId, days);
      onClose();
    } catch { /* error shown in chat via job state */ }
  };

  if (!open) return null;

  return createPortal(
    <Modal open onClose={onClose} title="Run Backtest" width="max-w-sm">
      <div className="space-y-3">
        {!symbol && (
          <div className="bg-amber/10 border border-amber/20 rounded-md p-2">
            <p className="text-[10px] text-amber font-semibold">Select a symbol in the form before running a backtest.</p>
          </div>
        )}

        {/* Period */}
        <div>
          <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Period</label>
          <div className="flex gap-1">
            {[7, 30, 365, 730].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`text-[10px] font-bold px-2 py-1 rounded-md transition-all cursor-pointer flex-1 ${
                  days === d
                    ? 'bg-orange-bg text-orange border border-orange/20'
                    : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
                }`}
              >
                {d === 7 ? '7d' : d === 30 ? '30d' : d === 365 ? '1y' : '2y'}
              </button>
            ))}
          </div>
        </div>

        {/* Capital */}
        <div>
          <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Capital</label>
          <input
            type="number"
            value={capital}
            onChange={(e) => setCapital(e.target.value)}
            placeholder="10000"
            className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] placeholder:text-ink-4"
          />
        </div>

        {/* Leverage */}
        <div>
          <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Leverage</label>
          <select
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none"
          >
            {[1, 50, 100, 200, 500, 1000, 2000].map((l) => (
              <option key={l} value={l}>1:{l}</option>
            ))}
          </select>
        </div>

        {/* Run Button */}
        <button
          type="button"
          onClick={handleRun}
          disabled={statelessBacktest.isPending || !symbol}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange text-white text-xs font-bold hover:bg-orange/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {statelessBacktest.isPending ? (
            <>
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
              Starting...
            </>
          ) : (
            <>
              <Play size={12} />
              Run Backtest
            </>
          )}
        </button>
      </div>
    </Modal>,
    document.body,
  );
}

// ─── Main Component ──────────────────────────────────

export function StrategyChatPanel({
  currentRules,
  symbol,
  onApplyRules,
  onSymbolChange,
  isOpen,
  onClose,
  isEdit,
  templateId,
  strategyId,
  strategyName,
  strategyDescription,
}: {
  currentRules: ConditionEngineRules;
  symbol: string;
  onApplyRules: (rules: ConditionEngineRules, explanation?: string) => void;
  onSymbolChange?: (symbol: string) => void;
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  templateId?: string;
  strategyId?: string;
  strategyName?: string;
  strategyDescription?: string;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [showBtModal, setShowBtModal] = useState(false);
  const [btModalParams, setBtModalParams] = useState<BacktestInitialParams | undefined>();
  const [activeTab, setActiveTab] = useState<'chat' | 'history'>('chat');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chat = useChatWithStrategy();

  // Load persisted chat session for existing strategies
  const { data: chatSession } = useChatSession(strategyId);
  const sessionId = chatSession?.id;
  const [sessionResumed, setSessionResumed] = useState(false);
  const hasHistory = !!chatSession?.messages?.length;

  const resumeSession = useCallback(() => {
    if (!chatSession?.messages?.length) return;

    const restored: ChatMessage[] = chatSession.messages.map((m: ChatSessionMessage) => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      rulesApplied: m.rules_applied,
      rulesSnapshot: m.rules_snapshot,
      previousRules: m.previous_rules,
      options: m.metadata?.suggestions || m.metadata?.options,
      keyPoints: m.metadata?.keyPoints,
      backtestJobId: m.metadata?.backtestJobId,
      backtestDays: m.metadata?.backtestDays,
      backtestSummary: m.metadata?.backtestSummary,
    }));

    // Detect if rules changed since last chat
    const lastRules = chatSession.rules_snapshot_at_end;
    if (lastRules && JSON.stringify(lastRules) !== JSON.stringify(currentRules)) {
      restored.push({
        id: 'rules-changed-notice',
        role: 'assistant',
        content: 'The strategy rules have been modified since your last conversation. I\'ll work with the updated rules.',
      });
    }

    setMessages(restored);
    setSessionResumed(true);

    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current!.scrollHeight, behavior: 'smooth' });
    }, 100);
  }, [chatSession, currentRules]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist messages across panel close/reopen (in-memory fallback)
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => {
    if (isOpen && messagesRef.current.length > 0 && messages.length === 0) {
      setMessages(messagesRef.current);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastAIRulesRef = useRef<string | null>(null);
  const pendingTypeRef = useRef<'forging' | 'analyzing' | 'thinking'>('forging');
  const latestBacktestReportRef = useRef<BacktestReport | null>(null);
  const backtestRulesSnapshotRef = useRef<string | null>(null);

  const presets = getPresetsForTemplate(isEdit ? undefined : templateId);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  }, []);

  // Auto-scroll to bottom on every message change or pending state
  useEffect(() => {
    scrollToBottom();
  }, [messages, chat.isPending, scrollToBottom]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const buildHistory = useCallback((): Array<{ role: 'user' | 'assistant'; content: string }> => {
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => {
        // Include backtest results in history so AI knows about past performance
        let content = m.content;
        if (m.backtestSummary) content += `\n\n[Backtest Results: ${m.backtestSummary}]`;
        return { role: m.role, content };
      });
  }, [messages]);

  const detectManualEdit = useCallback((): boolean => {
    if (!lastAIRulesRef.current) return false;
    return JSON.stringify(currentRules) !== lastAIRulesRef.current;
  }, [currentRules]);

  // Store backtest report when complete (no auto-analysis)
  const handleBacktestComplete = useCallback(
    (report: BacktestReport, days?: number) => {
      latestBacktestReportRef.current = report;
      backtestRulesSnapshotRef.current = JSON.stringify(currentRules);

      const periodLabel = days ? `${days} days` : 'backtest period';
      const summary = `${report.totalTrades} trades in ${periodLabel}, ${(report.winRate ?? 0).toFixed(1)}% win rate, ${report.netProfit >= 0 ? '+' : ''}$${(report.netProfit ?? 0).toFixed(2)} net P&L (${(report.returnPct ?? 0).toFixed(1)}% return), ${(report.maxDrawdown ?? 0).toFixed(1)}% max drawdown, ${(report.profitFactor ?? 0).toFixed(2)} profit factor, ${(report.sharpeRatio ?? 0).toFixed(2)} Sharpe ratio.${report.warnings?.length ? ' Warnings: ' + report.warnings.map(w => w.message).join('; ') : ''}${report.totalTrades === 0 ? ' WARNING: Zero trades executed — likely insufficient capital for margin requirements or no entry signals triggered.' : ''}`;

      setMessages((prev) => prev.map((m) =>
        m.backtestJobId ? { ...m, backtestSummary: summary } : m
      ));
    },
    [currentRules],
  );

  // Analyze backtest results with AI (triggered by user clicking button)
  const analyzeBacktestResults = useCallback(
    async (report: BacktestReport) => {
      const summary = `${report.totalTrades} trades, ${(report.winRate ?? 0).toFixed(1)}% win rate, ${report.netProfit >= 0 ? '+' : ''}$${(report.netProfit ?? 0).toFixed(2)} net P&L (${(report.returnPct ?? 0).toFixed(1)}% return), ${(report.maxDrawdown ?? 0).toFixed(1)}% max drawdown, ${(report.profitFactor ?? 0).toFixed(2)} profit factor, ${(report.sharpeRatio ?? 0).toFixed(2)} Sharpe ratio.${report.warnings?.length ? ' Warnings: ' + report.warnings.map(w => w.message).join('; ') : ''}${report.totalTrades === 0 ? ' WARNING: Zero trades executed — likely insufficient capital for margin requirements or no entry signals triggered.' : ''}`;

      try {
        pendingTypeRef.current = 'analyzing';
        const result = await chat.mutateAsync({
          prompt: `Analyze these backtest results and give me your assessment with key points: ${summary}`,
          currentRules,
          conversationHistory: [...buildHistory(), { role: 'assistant', content: `Backtest results: ${summary}` }],
          context: { symbol: symbol || undefined },
          backtestReport: slimReportForChat(report),
          sessionId,
        });

        if (result.type === 'analysis') {
          setMessages((prev) => [
            ...prev,
            {
              id: `analysis-${Date.now()}`,
              role: 'assistant',
              content: result.explanation,
              keyPoints: result.keyPoints,
              options: result.suggestions,
            },
          ]);
        } else {
          const content = 'explanation' in result ? result.explanation : 'question' in result ? result.question : 'message' in result ? result.message : '';
          if (content) {
            setMessages((prev) => [
              ...prev,
              { id: `a-${Date.now()}`, role: 'assistant', content },
            ]);
          }
        }
        scrollToBottom();
      } catch {
        // Silent fail — backtest results are already shown
      }
    },
    [chat, currentRules, symbol, buildHistory, scrollToBottom, sessionId],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || chat.isPending) return;

      const rulesModifiedManually = detectManualEdit();

      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role: 'user',
        content: text.trim(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      scrollToBottom();

      try {
        const lc = text.trim().toLowerCase();
        const isQuestion = /^(what|why|how|when|which|can|should|is|are|do|does|tell|explain|compare)\b/.test(lc) || lc.includes('?');
        pendingTypeRef.current = isQuestion ? 'thinking' : 'forging';
        const result: ChatResponse = await chat.mutateAsync({
          prompt: text.trim(),
          currentRules,
          conversationHistory: buildHistory(),
          context: {
            symbol: symbol || undefined,
            rulesModifiedManually,
            strategyName: strategyName || undefined,
            strategyDescription: strategyDescription || undefined,
          },
          backtestReport: latestBacktestReportRef.current && backtestRulesSnapshotRef.current === JSON.stringify(currentRules)
            ? slimReportForChat(latestBacktestReportRef.current)
            : undefined,
          sessionId,
        });

        if (result.type === 'backtest') {
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: result.explanation + (symbol ? '' : '\n\nPlease set a symbol in the form first.'),
              backtestAction: !!symbol,
            },
          ]);
          // Auto-open modal with AI-suggested params
          if (symbol && result.params) {
            setBtModalParams(result.params);
            setShowBtModal(true);
          }
          scrollToBottom();
        } else if (result.type === 'rules') {
          const assistantMsg: ChatMessage = {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: result.explanation,
            rulesApplied: true,
            rulesSnapshot: result.rules as ConditionEngineRules,
            previousRules: structuredClone(currentRules),
            options: result.suggestions,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          onApplyRules(result.rules as ConditionEngineRules, result.explanation);
          lastAIRulesRef.current = JSON.stringify(result.rules);
          if (result.suggestedSymbol && onSymbolChange) {
            onSymbolChange(result.suggestedSymbol);
          }
        } else if (result.type === 'analysis') {
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: result.explanation,
              keyPoints: result.keyPoints,
              options: result.suggestions,
            },
          ]);
        } else if (result.type === 'clarification') {
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: result.question,
              options: result.options,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}`, role: 'assistant', content: result.message },
          ]);
        }

        scrollToBottom();
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : 'Something went wrong';
        setMessages((prev) => [
          ...prev,
          { id: `e-${Date.now()}`, role: 'assistant', content: `Error: ${errMsg}` },
        ]);
        scrollToBottom();
      }
    },
    [chat, currentRules, symbol, buildHistory, onApplyRules, onSymbolChange, scrollToBottom, detectManualEdit],
  );

  const handleBacktestJobStarted = useCallback(
    (jobId: string, days: number) => {
      setMessages((prev) => [
        ...prev,
        { id: `bt-user-${Date.now()}`, role: 'user', content: `Run backtest on current strategy (${days} days)` },
        { id: `bt-${Date.now()}`, role: 'assistant', content: 'Running backtest...', backtestJobId: jobId, backtestDays: days },
      ]);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const handleUndoRules = useCallback(
    (msgId: string) => {
      const msg = messages.find((m) => m.id === msgId);
      if (msg?.previousRules) {
        onApplyRules(msg.previousRules);
        lastAIRulesRef.current = null;
        // Mark message as reverted
        setMessages((prev) => prev.map((m) =>
          m.id === msgId ? { ...m, rulesApplied: false } : m
        ));
      }
    },
    [messages, onApplyRules],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(!e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (!isOpen) return null;

  const isEmpty = messages.length === 0;

  // Build timeline from messages that applied rules
  const timelineEntries = messages
    .filter((m) => m.rulesApplied && m.rulesSnapshot)
    .map((m) => ({
      id: m.id,
      timestamp: parseInt(m.id.replace('a-', ''), 10) || Date.now(),
      explanation: m.content,
      rules: m.rulesSnapshot!,
      isActive: JSON.stringify(m.rulesSnapshot) === JSON.stringify(currentRules),
    }));

  const panel = (
    <div
      className={isMobile
        ? 'fixed inset-0 flex flex-col overflow-hidden bg-bg'
        : 'flex-1 min-w-[380px] flex flex-col glass-sm overflow-hidden rounded-lg h-[calc(100vh-13rem)] sticky top-6'
      }
      style={isMobile ? { zIndex: 9999 } : undefined}
    >
      {/* Header with tabs */}
      <div className="flex items-center border-b border-glass-border">
        <button
          type="button"
          onClick={() => setActiveTab('chat')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-bold transition-colors cursor-pointer ${
            activeTab === 'chat'
              ? 'text-orange border-b-2 border-orange'
              : 'text-ink-4 hover:text-ink-3'
          }`}
        >
          <MessageSquare size={11} />
          Chat
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('history')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-bold transition-colors cursor-pointer ${
            activeTab === 'history'
              ? 'text-orange border-b-2 border-orange'
              : 'text-ink-4 hover:text-ink-3'
          }`}
        >
          <History size={11} />
          History
          {timelineEntries.length > 0 && (
            <span className="bg-orange/20 text-orange text-[8px] font-bold px-1.5 py-0.5 rounded-full leading-none">
              {timelineEntries.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2.5 text-ink-4 hover:text-ink transition-colors cursor-pointer"
        >
          <X size={14} />
        </button>
      </div>

      {/* History tab */}
      {activeTab === 'history' && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {timelineEntries.length === 0 ? (
            <div className="text-center py-16">
              <History size={24} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs text-ink-4">No changes yet</p>
              <p className="text-[10px] text-ink-5 mt-1">AI modifications will appear here</p>
            </div>
          ) : (
            <div className="space-y-0">
              {timelineEntries.map((entry, idx) => (
                <div key={entry.id} className="relative pl-6 pb-4 last:pb-0">
                  {/* Timeline line */}
                  {idx < timelineEntries.length - 1 && (
                    <div className="absolute left-[7px] top-5 bottom-0 w-px bg-glass-border" />
                  )}
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-1 w-[15px] h-[15px] rounded-full border-2 flex items-center justify-center ${
                    entry.isActive
                      ? 'border-orange bg-orange/20'
                      : 'border-glass-border bg-surface-2'
                  }`}>
                    <Check size={8} className={entry.isActive ? 'text-orange' : 'text-ink-4'} />
                  </div>
                  {/* Content */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-ink-4 font-mono">
                        {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {entry.isActive && (
                        <span className="text-[8px] font-bold text-green bg-green/10 px-1.5 py-0.5 rounded leading-none">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-ink leading-snug mb-2">{renderMarkdown(entry.explanation)}</div>
                    <RulesPreviewCard rules={entry.rules} />
                    <div className="flex items-center gap-3 mt-2">
                      {!entry.isActive && (
                        <button
                          type="button"
                          onClick={() => {
                            onApplyRules(entry.rules);
                            lastAIRulesRef.current = JSON.stringify(entry.rules);
                          }}
                          className="flex items-center gap-1 text-[10px] font-bold text-ink-4 hover:text-orange transition-colors cursor-pointer"
                        >
                          <RotateCcw size={10} />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chat tab — Messages */}
      <div ref={scrollRef} className={`flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 ${activeTab !== 'chat' ? 'hidden' : ''}`}>
            {isEmpty && !isEdit && (
              <div className="space-y-3">
                <p className="text-xs text-ink-3 text-center">
                  Describe what you want and I'll generate the rules
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => sendMessage(p)}
                      disabled={chat.isPending}
                      className="text-xs font-medium px-2.5 py-1.5 rounded-full border border-glass-border hover:border-orange/40 hover:bg-orange/5 text-ink-3 hover:text-orange transition-all cursor-pointer disabled:opacity-50"
                    >
                      <Zap size={9} className="inline mr-1 opacity-60" />
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isEmpty && isEdit && !hasHistory && (
              <p className="text-xs text-ink-3 text-center pt-4">
                Ask me to modify your strategy — e.g., "add a trailing stop" or "widen the grid step"
              </p>
            )}

            {isEmpty && isEdit && hasHistory && !sessionResumed && (
              <div className="flex flex-col items-center justify-center pt-8 pb-4 gap-3">
                <div className="w-12 h-12 rounded-full bg-orange/10 flex items-center justify-center">
                  <History size={22} className="text-orange" />
                </div>
                <div className="text-center space-y-1">
                  <p className="text-sm font-semibold text-ink">You have a previous session</p>
                  <p className="text-[11px] text-ink-3 max-w-[260px]">
                    Pick up where you left off with your full conversation history and rule changes.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resumeSession}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-orange to-amber text-white text-xs font-bold shadow-glow-sm hover:shadow-glow transition-shadow cursor-pointer"
                >
                  <Zap size={14} />
                  Continue Forging
                </button>
                <button
                  type="button"
                  onClick={() => setSessionResumed(true)}
                  className="text-[10px] text-ink-4 hover:text-ink-2 transition-colors cursor-pointer"
                >
                  Start fresh instead
                </button>
              </div>
            )}

            {messages.map((msg) => {
              const showBtBar = !msg.keyPoints && (msg.backtestAction || msg.rulesApplied || msg.options?.some((s) => /backtest|test.*strat|simulate|run.*test/i.test(s))) && symbol;
              return (
              <div
                key={msg.id}
                className={`flex ${msg.backtestJobId ? 'w-full' : msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-lg overflow-hidden text-sm leading-relaxed ${
                    msg.backtestJobId
                      ? 'w-full bg-surface-2 text-ink'
                      : msg.role === 'user'
                        ? 'max-w-[90%] bg-orange/15 text-ink'
                        : 'max-w-[90%] bg-surface-2 text-ink'
                  }`}
                >
                  <div className="px-3 py-2">
                    <div className="whitespace-pre-wrap">{msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}</div>

                    {msg.rulesApplied && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] font-bold text-green bg-green/10 px-2 py-0.5 rounded">
                          Applied to form
                        </span>
                        {msg.previousRules && (
                          <button
                            type="button"
                            onClick={() => handleUndoRules(msg.id)}
                            className="flex items-center gap-1 text-[10px] font-bold text-ink-4 hover:text-orange transition-colors cursor-pointer"
                          >
                            <Undo2 size={10} />
                            Undo
                          </button>
                        )}
                      </div>
                    )}

                    {/* Rules preview */}
                    {msg.rulesSnapshot && (
                      <RulesPreviewCard rules={msg.rulesSnapshot} />
                    )}

                    {/* Analysis key points */}
                    {msg.keyPoints && msg.keyPoints.length > 0 && (
                      <AnalysisCard keyPoints={msg.keyPoints} />
                    )}

                    {/* Backtest result */}
                    {msg.backtestJobId && (
                      <BacktestResultCard jobId={msg.backtestJobId} onComplete={(report) => handleBacktestComplete(report, msg.backtestDays)} onAnalyze={analyzeBacktestResults} />
                    )}

                    {/* Suggestion chips — inline, send on click */}
                    {msg.options && msg.options.length > 0 && (() => {
                      const isBacktest = (s: string) => /backtest|test.*strat|simulate|run.*test/i.test(s);
                      const otherChips = msg.options.filter((o) => !isBacktest(o));
                      return otherChips.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {otherChips.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => sendMessage(opt)}
                              disabled={chat.isPending}
                              className="text-xs font-medium px-2.5 py-1 rounded-full bg-surface border border-glass-border hover:border-orange/40 hover:text-orange text-ink-3 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Run Backtest — full-width bar matching "View trades" style */}
                  {showBtBar && (
                    <button
                      type="button"
                      onClick={() => setShowBtModal(true)}
                      className="w-full text-xs text-orange font-semibold cursor-pointer flex items-center justify-center gap-1.5 py-2.5 border-t border-glass-border/50 hover:bg-white/[0.02] transition-colors outline-none focus:outline-none"
                    >
                      <FlaskConical size={10} />
                      Run Backtest
                    </button>
                  )}
                </div>
              </div>
              );
            })}

            {chat.isPending && (
              <div className="flex justify-start">
                <div className="bg-surface-2 rounded-lg px-3 py-2.5 flex items-center gap-2.5">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 bg-orange rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                  <span className="text-xs text-ink-3 font-medium animate-[forge-text-pulse_1.5s_ease-in-out_infinite]">
                    {pendingTypeRef.current === 'analyzing' ? 'Analyzing results' : pendingTypeRef.current === 'thinking' ? 'Thinking' : 'Forging strategy'}
                    <span className="forge-dots" />
                  </span>
                </div>
              </div>
            )}
          </div>

      {/* Input (chat tab only) */}
      <div className={`flex-shrink-0 px-3 py-3 border-t border-glass-border ${activeTab !== 'chat' ? 'hidden' : ''}`}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isEdit ? 'Describe a change...' : 'Describe your strategy...'}
            rows={1}
            disabled={chat.isPending}
            className="flex-1 bg-surface border border-glass-border rounded-lg px-3 py-2.5 text-sm font-mono text-ink placeholder:text-ink-4 outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] resize-none min-h-[42px] max-h-[120px] disabled:opacity-50"
            style={{ height: 'auto', overflow: 'hidden' }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 120) + 'px';
            }}
          />
          <button
            type="button"
            onClick={() => setShowBtModal(true)}
            title="Run Backtest"
            className="p-2 rounded-lg bg-surface border border-glass-border text-ink-3 hover:text-orange hover:border-orange/40 transition-colors cursor-pointer"
          >
            <FlaskConical size={14} />
          </button>
          <button
            type="button"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || chat.isPending}
            className="p-2 rounded-lg bg-orange text-white hover:bg-orange/90 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <Send size={14} />
          </button>
        </div>
      </div>

      {/* Backtest Modal */}
      <BacktestModal
        open={showBtModal}
        onClose={() => { setShowBtModal(false); setBtModalParams(undefined); }}
        symbol={symbol}
        currentRules={currentRules}
        onJobStarted={handleBacktestJobStarted}
        initialParams={btModalParams}
      />
    </div>
  );

  return isMobile ? createPortal(panel, document.body) : panel;
}
